import { useState } from 'react'
import { Outlet } from 'react-router'
import type { PbPortalSettingsBranding } from '@itsrever/rever-api'
import { useSlug } from '@/hooks'
import { ScrollToTop } from '@/components/ScrollToTop'
import { Toaster, ThemeProvider } from '@itsrever/design-system'
import { UnstyledLoading } from '@/components/Loading'
import { Error, NotFound } from '@/pages'
import { useOrderShortcut } from '@/hooks/use-order-shortcut'
import { useCustomCopies } from '@/hooks/use-custom-copies'
import { usePageVisit } from '@/hooks/use-page-view'
import { useBugReport } from '@/hooks/use-bug-report'
import { useSettings } from '@/hooks/use-settings'
import { DemoSettings } from '@/app/demo/demo-settings'

const getThemeConfig = (theme?: PbPortalSettingsBranding) => {
    return {
        colors: {
            primary: {
                main: theme?.primary_theme_color_main,
                light: theme?.primary_theme_color_light,
                dark: theme?.primary_theme_color_dark,
                contrastText: theme?.primary_theme_color_contrast_text
            },
            secondary: {
                main: theme?.secondary_theme_color_main,
                light: theme?.secondary_theme_color_light,
                dark: theme?.secondary_theme_color_dark,
                contrastText: theme?.secondary_theme_color_contrast_text
            }
        },
        borderRadius: theme?.border_radius,
        inputVariant: theme?.input_variant
    }
}

export function AppLayout() {
    usePageVisit()
    useBugReport()
    useOrderShortcut()

    const [theme, setTheme] = useState<PbPortalSettingsBranding>()

    const slug = useSlug()

    const { data: settings, status: settingsStatus } = useSettings({ slug })
    const { status: copiesStatus } = useCustomCopies({ slug })

    const updateTheme = (theme: Partial<PbPortalSettingsBranding>) => {
        setTheme((prev) => ({ ...prev, ...theme }))

        if (theme?.title) {
            document.title = theme?.title
        }

        if (theme?.favicon_url) {
            const oldFavicon = document.getElementById('dynamic-favicon')
            if (oldFavicon) document.head.removeChild(oldFavicon)

            const favicon = document.createElement('link')
            favicon.id = 'dynamic-favicon'
            favicon.rel = 'shortcut icon'
            favicon.href = theme?.favicon_url
            document.head.appendChild(favicon)
        }
    }

    if (settingsStatus === 'pending' || copiesStatus === 'pending')
        return <UnstyledLoading loading={true} />

    if (settingsStatus === 'error' || !settings.is_enabled) return <NotFound />

    if (copiesStatus === 'error') return <Error />

    return (
        <ScrollToTop>
            <ThemeProvider
                theme={
                    theme
                        ? getThemeConfig(theme)
                        : getThemeConfig(settings.branding)
                }
            >
                <DemoSettings onThemeUpdate={updateTheme} />
                <Toaster />
                <Outlet />
            </ThemeProvider>
        </ScrollToTop>
    )
}
